import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Link, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { 
  MessageSquare, Mail, Building, Clock, Trash2, 
  MailOpen, Archive, ArrowLeft, RefreshCw, User
} from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../../components/ui/dropdown-menu';
import { toast } from 'sonner';
import axios from 'axios';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_property-dispatch/artifacts/zkteztq8_OrchardLMS%20Logo.png";

const MessagesPage = () => {
  const { user, token, API, logout, isPlatformAdmin } = useAuth();
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [stats, setStats] = useState({ total: 0, unread: 0 });
  const [selectedMessage, setSelectedMessage] = useState(null);

  useEffect(() => {
    if (!isPlatformAdmin()) {
      navigate('/platform/login');
      return;
    }
    fetchMessages();
  }, [user, navigate, filter]);

  const fetchMessages = async () => {
    try {
      setLoading(true);
      const params = filter !== 'all' ? `?status=${filter}` : '';
      const response = await axios.get(`${API}/platform/messages${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessages(response.data.messages || []);
      setStats({ total: response.data.total, unread: response.data.unread });
    } catch (error) {
      toast.error('Failed to fetch messages');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (messageId, status) => {
    try {
      await axios.patch(`${API}/platform/messages/${messageId}?status=${status}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Message updated');
      fetchMessages();
      if (selectedMessage?.id === messageId) {
        setSelectedMessage({ ...selectedMessage, status });
      }
    } catch (error) {
      toast.error('Failed to update message');
    }
  };

  const deleteMessage = async (messageId) => {
    if (!window.confirm('Are you sure you want to delete this message?')) return;
    try {
      await axios.delete(`${API}/platform/messages/${messageId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Message deleted');
      setSelectedMessage(null);
      fetchMessages();
    } catch (error) {
      toast.error('Failed to delete message');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffHours = (now - date) / (1000 * 60 * 60);
    
    if (diffHours < 24) {
      return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    } else if (diffHours < 48) {
      return 'Yesterday';
    } else {
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }
  };

  const navItems = [
    { path: '/platform', label: 'Dashboard' },
    { path: '/platform/tenants', label: 'Tenants' },
    { path: '/platform/messages', label: 'Messages', badge: stats.unread },
    { path: '/platform/financing-requests', label: 'Financing' },
    { path: '/platform/analytics', label: 'Analytics' }
  ];

  return (
    <div className="min-h-screen bg-gray-900">
      {/* Header */}
      <header className="bg-gray-800 border-b border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-8">
              <Link to="/platform" className="flex items-center gap-2">
                <img src={LOGO_URL} alt="Orchard LMS" className="h-8 w-auto" />
              </Link>
              <nav className="hidden md:flex items-center gap-6">
                {navItems.map((item) => (
                  <Link 
                    key={item.path}
                    to={item.path} 
                    className={`text-sm font-medium transition-colors flex items-center gap-2 ${
                      item.path === '/platform/messages' 
                        ? 'text-white' 
                        : 'text-gray-300 hover:text-white'
                    }`}
                  >
                    {item.label}
                    {item.badge > 0 && (
                      <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
                        {item.badge}
                      </span>
                    )}
                  </Link>
                ))}
              </nav>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-right">
                <p className="text-sm text-white">{user?.name || user?.email}</p>
                <p className="text-xs text-gray-400">Platform Admin</p>
              </div>
              <Button variant="ghost" size="sm" onClick={handleLogout} className="text-gray-300 hover:text-white">
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-white">Messages</h1>
            <p className="text-gray-400">Contact form submissions from the website</p>
          </div>
          <Button variant="outline" onClick={fetchMessages} className="border-gray-600 text-gray-300 hover:bg-gray-800">
            <RefreshCw className="h-4 w-4 mr-2" /> Refresh
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <Card className="bg-gray-800 border-gray-700">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-400">Total Messages</p>
                  <p className="text-2xl font-bold text-white">{stats.total}</p>
                </div>
                <MessageSquare className="h-8 w-8 text-gray-600" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gray-800 border-gray-700">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-400">Unread</p>
                  <p className="text-2xl font-bold text-amber-500">{stats.unread}</p>
                </div>
                <Mail className="h-8 w-8 text-amber-500/30" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gray-800 border-gray-700">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-400">Read</p>
                  <p className="text-2xl font-bold text-emerald-500">{stats.total - stats.unread}</p>
                </div>
                <MailOpen className="h-8 w-8 text-emerald-500/30" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filter Tabs */}
        <div className="flex gap-2 mb-4">
          {['all', 'unread', 'read', 'archived'].map((f) => (
            <Button
              key={f}
              variant={filter === f ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setFilter(f)}
              className={filter === f ? 'bg-primary' : 'text-gray-400 hover:text-white hover:bg-gray-800'}
            >
              {f.charAt(0).toUpperCase() + f.slice(1)}
            </Button>
          ))}
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Messages List */}
          <div className="lg:col-span-1">
            <Card className="bg-gray-800 border-gray-700">
              <CardContent className="p-0">
                {loading ? (
                  <div className="flex items-center justify-center h-64">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                  </div>
                ) : messages.length === 0 ? (
                  <div className="text-center py-12">
                    <MessageSquare className="h-12 w-12 text-gray-600 mx-auto mb-4" />
                    <p className="text-gray-400">No messages found</p>
                  </div>
                ) : (
                  <div className="divide-y divide-gray-700">
                    {messages.map((msg) => (
                      <div
                        key={msg.id}
                        onClick={() => {
                          setSelectedMessage(msg);
                          if (msg.status === 'unread') {
                            updateStatus(msg.id, 'read');
                          }
                        }}
                        className={`p-4 cursor-pointer transition-colors ${
                          selectedMessage?.id === msg.id 
                            ? 'bg-gray-700' 
                            : 'hover:bg-gray-750 hover:bg-gray-700/50'
                        } ${msg.status === 'unread' ? 'border-l-2 border-primary' : ''}`}
                      >
                        <div className="flex items-start justify-between mb-1">
                          <p className={`font-medium truncate ${msg.status === 'unread' ? 'text-white' : 'text-gray-300'}`}>
                            {msg.name}
                          </p>
                          <span className="text-xs text-gray-500 flex-shrink-0 ml-2">
                            {formatDate(msg.created_at)}
                          </span>
                        </div>
                        <p className="text-sm text-gray-400 truncate">{msg.email}</p>
                        <p className="text-sm text-gray-500 truncate mt-1">{msg.message}</p>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Message Detail */}
          <div className="lg:col-span-2">
            {selectedMessage ? (
              <Card className="bg-gray-800 border-gray-700">
                <CardHeader className="border-b border-gray-700">
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-white flex items-center gap-2">
                        <User className="h-5 w-5 text-gray-400" />
                        {selectedMessage.name}
                      </CardTitle>
                      <p className="text-gray-400 text-sm mt-1">{selectedMessage.email}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="outline" size="sm" className="border-gray-600 text-gray-300">
                            {selectedMessage.status === 'unread' ? (
                              <><Mail className="h-4 w-4 mr-2" /> Unread</>
                            ) : selectedMessage.status === 'archived' ? (
                              <><Archive className="h-4 w-4 mr-2" /> Archived</>
                            ) : (
                              <><MailOpen className="h-4 w-4 mr-2" /> Read</>
                            )}
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent>
                          <DropdownMenuItem onClick={() => updateStatus(selectedMessage.id, 'unread')}>
                            <Mail className="h-4 w-4 mr-2" /> Mark as Unread
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => updateStatus(selectedMessage.id, 'read')}>
                            <MailOpen className="h-4 w-4 mr-2" /> Mark as Read
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => updateStatus(selectedMessage.id, 'archived')}>
                            <Archive className="h-4 w-4 mr-2" /> Archive
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => deleteMessage(selectedMessage.id)}
                        className="text-red-400 hover:text-red-300 hover:bg-red-900/20"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="pt-6">
                  <div className="space-y-4">
                    {selectedMessage.company && (
                      <div className="flex items-center gap-2 text-gray-400">
                        <Building className="h-4 w-4" />
                        <span>{selectedMessage.company}</span>
                      </div>
                    )}
                    <div className="flex items-center gap-2 text-gray-400">
                      <Clock className="h-4 w-4" />
                      <span>{new Date(selectedMessage.created_at).toLocaleString()}</span>
                    </div>
                    <div className="border-t border-gray-700 pt-4 mt-4">
                      <p className="text-gray-300 whitespace-pre-wrap">{selectedMessage.message}</p>
                    </div>
                    <div className="border-t border-gray-700 pt-4 mt-4">
                      <a 
                        href={`mailto:${selectedMessage.email}?subject=Re: Your inquiry about Orchard LMS`}
                        className="inline-flex items-center gap-2 text-primary hover:underline"
                      >
                        <Mail className="h-4 w-4" />
                        Reply via Email
                      </a>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card className="bg-gray-800 border-gray-700 h-full">
                <CardContent className="flex items-center justify-center h-64">
                  <div className="text-center">
                    <MessageSquare className="h-12 w-12 text-gray-600 mx-auto mb-4" />
                    <p className="text-gray-400">Select a message to view details</p>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default MessagesPage;
